/**
 * @file DialogManageSessions Component
 * @description This component renders a dialog for managing user sessions across different devices.
 * It displays a list of active and previously active devices, allowing users to log out from individual devices or all devices at once.
 */

import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DialogWrapper from './dialog-wrapper';
import { getColor } from '../../assets/theme';
import {
  DesktopWindowsOutlined,
  DevicesOutlined,
  LaptopMacOutlined,
  Logout,
  PhoneIphoneOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import useConfirmationDialog from '../../hooks/useConfirmationDialog';

/**
 * @interface DialogManageSessions
 * @property {boolean} open - Determines whether the dialog is open or closed
 * @property {() => void} onClose - Function to call when the dialog should be closed
 */
interface DialogManageSessions {
  open: boolean;
  onClose: () => void;
}

/**
 * DialogManageSessions component
 * @param {DialogManageSessions} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const DialogManageSessions = ({ open, onClose }: DialogManageSessions) => {
  const { white, primary, textSecondary, borderColor } = getColor();

  const { DialogComponentV2, onShowDialog } = useConfirmationDialog();

  return (
    <>
      <DialogWrapper open={open} onClose={onClose}>
        <Box>
          <Stack
            sx={{
              bgcolor: white,
              borderRadius: '4px',
              p: '24px',
              justifyContent: 'flex-start',
            }}
          >
            <DevicesOutlined
              sx={{
                width: '59px',
                height: '59px',
                bgcolor: '#F7F7FB',
                p: '12px',
                borderRadius: '100%',
                color: primary,
              }}
            />
            <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
              Your Devices
            </DialogTitle>
            <DialogContent sx={{ p: '16px 0!important' }}>
              <Stack gap='16px' mb='16px'>
                {/* Active device section */}
                {[
                  {
                    title: 'Dell 24',
                    description:
                      'Angeles, Pampanga PH - Dec 14, 2023 at 2:30 PM',
                    device: 'laptop',
                  },
                ].map((item: any, i: number) => {
                  return (
                    <ListItem
                      key={i}
                      sx={{
                        borderTop: `1px solid ${borderColor}`,
                        borderBottom: `1px solid ${borderColor}`,
                        gap: '16px',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        {item?.device == 'desktop' ? (
                          <DesktopWindowsOutlined sx={{ color: primary }} />
                        ) : item?.device == 'laptop' ? (
                          <LaptopMacOutlined sx={{ color: primary }} />
                        ) : item?.device == 'phone' ? (
                          <PhoneIphoneOutlined sx={{ color: primary }} />
                        ) : (
                          <DesktopWindowsOutlined sx={{ color: primary }} />
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        <Stack direction='row' alignItems='center' gap='16px'>
                          <Stack>
                            <Typography>
                              {item?.title}{' '}
                              <Chip
                                label='Active Now'
                                sx={{
                                  color: white,
                                  bgcolor: primary,
                                  fontSize: '12px',
                                  p: '3px 4px',
                                  cursor: 'default',
                                  ml: '10px',
                                  height: '28px',
                                  ':hover': {
                                    bgcolor: primary,
                                  },
                                  '.MuiChip-label': {
                                    p: '0 6px',
                                  },
                                }}
                              />
                            </Typography>
                            <Typography sx={{ color: textSecondary }}>
                              {item?.description}
                            </Typography>
                          </Stack>
                          <IconButton
                            sx={{
                              border: 0,
                              width: '34px',
                              height: '34px',
                              p: '5px',
                            }}
                            disableRipple
                            onClick={onShowDialog}
                          >
                            <Logout />
                          </IconButton>
                        </Stack>
                      </ListItemText>
                    </ListItem>
                  );
                })}
                <Typography sx={{ color: textSecondary }}>
                  Your devices linked to this account
                </Typography>
                <LoadingButton variant='contained' onClick={onShowDialog}>
                  Remove all other verified devices
                </LoadingButton>
              </Stack>
              {/* Previously active devices section */}
              <List>
                {[
                  {
                    title: 'Dell 24',
                    description:
                      'Angeles, Pampanga PH - Dec 14, 2023 at 2:30 PM',
                    device: 'laptop',
                  },
                  {
                    title: 'Iphone 14 Pro Max',
                    description:
                      'Angeles, Pampanga PH - Dec 14, 2023 at 2:30 PM',
                    device: 'phone',
                  },
                  {
                    title: 'Samsung Galaxy S 22 Ultra',
                    description:
                      'Angeles, Pampanga PH - Dec 14, 2023 at 2:30 PM',
                    device: 'phone',
                  },
                ].map((item: any, i: number) => {
                  return (
                    <ListItem
                      key={i}
                      sx={{
                        borderTop: `1px solid ${borderColor}`,
                        gap: '16px',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        {item?.device == 'desktop' ? (
                          <DesktopWindowsOutlined sx={{ color: primary }} />
                        ) : item?.device == 'laptop' ? (
                          <LaptopMacOutlined sx={{ color: primary }} />
                        ) : item?.device == 'phone' ? (
                          <PhoneIphoneOutlined sx={{ color: primary }} />
                        ) : (
                          <DesktopWindowsOutlined sx={{ color: primary }} />
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        <Stack direction='row' alignItems='center' gap='16px'>
                          <Stack>
                            <Typography>{item?.title}</Typography>
                            <Typography sx={{ color: textSecondary }}>
                              {item?.description}
                            </Typography>
                          </Stack>
                          <IconButton
                            sx={{
                              border: 0,
                              width: '34px',
                              height: '34px',
                              p: '5px',
                            }}
                            disableRipple
                            onClick={onShowDialog}
                          >
                            <Logout />
                          </IconButton>
                        </Stack>
                      </ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            </DialogContent>
            <DialogActions sx={{ p: '0 0 8px 0' }}>
              <Button variant='outlined' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </Stack>
        </Box>
      </DialogWrapper>

      {/* Confirmation dialog for logging out */}
      <DialogComponentV2 open={false} onConfirm={async () => {}} />
    </>
  );
};

export default DialogManageSessions;
